/**
 * Three Card Stud Store - REAL Poker with Blinds and Betting Rounds
 * Proper poker mechanics: Small/Big Blind, Call/Raise/Fold betting
 */

import { create } from 'zustand';
import { 
  createDeck, 
  shuffleDeck, 
  dealCards, 
  evaluateThreeCardHand,
  compareThreeCardHands,
  PokerSounds
} from './pokerEngine.js';
import { STARTING_CHIPS, AI_NAMES } from './pokerConstants.js';

const BLIND_LEVELS = {
  SMALL: 5,
  BIG: 10
};

const GAME_PHASES = {
  WAITING: 'waiting',      // Waiting to start
  BLINDS: 'blinds',        // Posting blinds
  DEALING: 'dealing',      // Cards being dealt
  BETTING: 'betting',      // Betting round
  SHOWDOWN: 'showdown',    // Reveal cards
  RESULT: 'result'         // Show winner, distribute pot
};

const ACTIONS = {
  FOLD: 'fold',
  CHECK: 'check',
  CALL: 'call',
  RAISE: 'raise',
  ALL_IN: 'all_in'
};

// Create a player object
const createPlayer = (id, name, chips, isHuman = false) => ({
  id,
  name,
  chips,
  isHuman,
  cards: [],
  hand: null,
  currentBet: 0,      // Amount bet this round
  totalBet: 0,        // Total amount in pot
  folded: false,
  isAllIn: false,
  isDealer: false,
  isSmallBlind: false,
  isBigBlind: false,
  hasActed: false     // Has acted this betting round
});

const INITIAL_STATE = {
  // Game setup
  gameMode: null,
  gamePhase: GAME_PHASES.WAITING,
  
  // Players (index 0 is always the human player)
  players: [],
  dealerIndex: 0,
  currentPlayerIndex: 0,
  
  // Betting
  pot: 0,
  currentBet: 0,       // Current bet to match
  minRaise: BLIND_LEVELS.BIG,
  lastRaiseAmount: BLIND_LEVELS.BIG,
  
  // Blinds
  smallBlind: BLIND_LEVELS.SMALL,
  bigBlind: BLIND_LEVELS.BIG,
  
  // Deck
  deck: [],
  
  // UI
  message: '',
  actionLog: [],
  showdownRevealed: false,
  winners: [],
  
  // Stats
  handsPlayed: 0,
  handsWon: 0
};

const useThreeCardStore = create((set, get) => ({
  ...INITIAL_STATE,
  
  // Initialize game with players
  setGameMode: (mode) => {
    const players = [createPlayer(0, 'You', STARTING_CHIPS, true)];
    
    if (mode === 'ai_opponents') {
      const shuffledNames = [...AI_NAMES].sort(() => Math.random() - 0.5);
      for (let i = 0; i < 3; i++) {
        players.push(createPlayer(i + 1, shuffledNames[i], STARTING_CHIPS, false));
      }
    } else if (mode === 'dealer') {
      // vs Dealer mode - just you vs one AI dealer
      players.push(createPlayer(1, 'Dealer', STARTING_CHIPS * 10, false));
    }
    
    // Set initial dealer
    players[0].isDealer = true;
    
    set({
      ...INITIAL_STATE,
      gameMode: mode,
      players,
      gamePhase: GAME_PHASES.WAITING,
      message: 'Click "Deal" to start a new hand'
    });
  },
  
  // Start a new hand
  startHand: () => {
    const { players, dealerIndex, smallBlind, bigBlind } = get();
    
    // Reset player states
    const resetPlayers = players.map((p, idx) => ({
      ...p,
      cards: [],
      hand: null,
      currentBet: 0,
      totalBet: 0,
      folded: false,
      isAllIn: false,
      hasActed: false,
      isDealer: false,
      isSmallBlind: false,
      isBigBlind: false
    }));
    
    // Rotate dealer
    const activePlayers = resetPlayers.filter(p => p.chips > 0);
    if (activePlayers.length < 2) {
      set({ message: 'Not enough players with chips!' });
      return;
    }
    
    const newDealerIndex = (dealerIndex + 1) % resetPlayers.length;
    const sbIndex = (newDealerIndex + 1) % resetPlayers.length;
    const bbIndex = (newDealerIndex + 2) % resetPlayers.length;
    
    resetPlayers[newDealerIndex].isDealer = true;
    resetPlayers[sbIndex].isSmallBlind = true;
    resetPlayers[bbIndex].isBigBlind = true;
    
    // Post blinds
    const sbAmount = Math.min(smallBlind, resetPlayers[sbIndex].chips);
    const bbAmount = Math.min(bigBlind, resetPlayers[bbIndex].chips);
    
    resetPlayers[sbIndex].chips -= sbAmount;
    resetPlayers[sbIndex].currentBet = sbAmount;
    resetPlayers[sbIndex].totalBet = sbAmount;
    if (resetPlayers[sbIndex].chips === 0) resetPlayers[sbIndex].isAllIn = true;
    
    resetPlayers[bbIndex].chips -= bbAmount;
    resetPlayers[bbIndex].currentBet = bbAmount;
    resetPlayers[bbIndex].totalBet = bbAmount;
    if (resetPlayers[bbIndex].chips === 0) resetPlayers[bbIndex].isAllIn = true;
    
    const pot = sbAmount + bbAmount;
    
    PokerSounds.playChip();
    
    set({
      players: resetPlayers,
      dealerIndex: newDealerIndex,
      pot,
      currentBet: bbAmount,
      minRaise: bigBlind,
      lastRaiseAmount: bigBlind,
      gamePhase: GAME_PHASES.BLINDS,
      actionLog: [
        `${resetPlayers[sbIndex].name} posts small blind (${sbAmount})`,
        `${resetPlayers[bbIndex].name} posts big blind (${bbAmount})`
      ],
      message: 'Blinds posted, dealing cards...',
      showdownRevealed: false,
      winners: []
    });
    
    // Deal cards after brief delay
    setTimeout(() => get().dealCards(), 800);
  },
  
  // Deal cards to all players
  dealCards: () => {
    const { players } = get();
    
    let deck = shuffleDeck(createDeck());
    
    const dealtPlayers = players.map(p => {
      if (p.folded || p.chips === 0 && p.totalBet === 0) return p;
      
      const { dealt, remaining } = dealCards(deck, 3);
      deck = remaining;
      
      return { ...p, cards: dealt };
    });
    
    PokerSounds.playShuffle();
    
    set({
      players: dealtPlayers,
      deck,
      gamePhase: GAME_PHASES.DEALING,
      message: 'Cards dealt!'
    });
    
    // Start betting after deal animation
    setTimeout(() => get().startBettingRound(), 1000);
  },
  
  // Start betting round
  startBettingRound: () => {
    const { players, dealerIndex } = get();
    
    // First to act is left of big blind (or left of dealer in heads-up)
    const bbIndex = players.findIndex(p => p.isBigBlind);
    let firstToAct = (bbIndex + 1) % players.length;
    
    // Skip folded/all-in players
    let attempts = 0;
    while ((players[firstToAct].folded || players[firstToAct].isAllIn) && attempts < players.length) {
      firstToAct = (firstToAct + 1) % players.length;
      attempts++;
    }
    
    // Reset hasActed for all players
    const resetPlayers = players.map(p => ({ ...p, hasActed: false }));
    
    set({
      players: resetPlayers,
      currentPlayerIndex: firstToAct,
      gamePhase: GAME_PHASES.BETTING,
      message: `${resetPlayers[firstToAct].name}'s turn`
    });
    
    // If AI's turn, trigger AI action
    if (!resetPlayers[firstToAct].isHuman) {
      setTimeout(() => get().aiAction(), 1000);
    }
  },
  
  // Get valid actions for current player
  getValidActions: () => {
    const { players, currentPlayerIndex, currentBet } = get();
    const player = players[currentPlayerIndex];
    
    if (!player || player.folded || player.isAllIn) return [];
    
    const actions = [ACTIONS.FOLD];
    const toCall = currentBet - player.currentBet;
    
    if (toCall === 0) {
      actions.push(ACTIONS.CHECK);
    } else if (toCall > 0 && player.chips >= toCall) {
      actions.push(ACTIONS.CALL);
    }
    
    // Can raise if has chips beyond call amount
    if (player.chips > toCall) {
      actions.push(ACTIONS.RAISE);
    }
    
    // All-in is always available if player has chips
    if (player.chips > 0) {
      actions.push(ACTIONS.ALL_IN);
    }
    
    return actions;
  },
  
  // Player action
  playerAction: (action, raiseAmount = 0) => {
    const { players, currentPlayerIndex, currentBet, pot, minRaise, bigBlind, actionLog } = get();
    const player = players[currentPlayerIndex];
    
    if (!player.isHuman) return; // Safety check
    
    get().executeAction(action, raiseAmount);
  },
  
  // Execute an action (works for both human and AI)
  executeAction: (action, raiseAmount = 0) => {
    const { players, currentPlayerIndex, currentBet, pot, minRaise, bigBlind, actionLog, lastRaiseAmount } = get();
    const player = { ...players[currentPlayerIndex] };
    const toCall = currentBet - player.currentBet;
    
    let newPot = pot;
    let newCurrentBet = currentBet;
    let newMinRaise = minRaise;
    let newLastRaise = lastRaiseAmount;
    const newLog = [...actionLog];
    
    switch (action) {
      case ACTIONS.FOLD:
        player.folded = true;
        newLog.push(`${player.name} folds`);
        PokerSounds.playDeal();
        break;
        
      case ACTIONS.CHECK:
        newLog.push(`${player.name} checks`);
        PokerSounds.playChip();
        break;
        
      case ACTIONS.CALL:
        const callAmount = Math.min(toCall, player.chips);
        player.chips -= callAmount;
        player.currentBet += callAmount;
        player.totalBet += callAmount;
        newPot += callAmount;
        if (player.chips === 0) player.isAllIn = true;
        newLog.push(`${player.name} calls ${callAmount}`);
        PokerSounds.playChip();
        break;
        
      case ACTIONS.RAISE:
        const totalRaise = toCall + raiseAmount;
        const actualRaise = Math.min(totalRaise, player.chips);
        player.chips -= actualRaise;
        player.currentBet += actualRaise;
        player.totalBet += actualRaise;
        newPot += actualRaise;
        newCurrentBet = player.currentBet;
        newLastRaise = raiseAmount;
        newMinRaise = Math.max(bigBlind, raiseAmount);
        if (player.chips === 0) player.isAllIn = true;
        newLog.push(`${player.name} raises to ${player.currentBet}`);
        PokerSounds.playChip();
        // Reset hasActed for others since there's a raise
        break;
        
      case ACTIONS.ALL_IN:
        const allInAmount = player.chips;
        player.totalBet += allInAmount;
        player.currentBet += allInAmount;
        newPot += allInAmount;
        player.chips = 0;
        player.isAllIn = true;
        if (player.currentBet > newCurrentBet) {
          newCurrentBet = player.currentBet;
          newLastRaise = player.currentBet - currentBet;
          newMinRaise = Math.max(bigBlind, newLastRaise);
        }
        newLog.push(`${player.name} goes ALL IN (${allInAmount})`);
        PokerSounds.playChip();
        break;
    }
    
    player.hasActed = true;
    
    // Update players array
    const newPlayers = [...players];
    newPlayers[currentPlayerIndex] = player;
    
    // If there was a raise, reset hasActed for others
    if (action === ACTIONS.RAISE || (action === ACTIONS.ALL_IN && player.currentBet > currentBet)) {
      newPlayers.forEach((p, i) => {
        if (i !== currentPlayerIndex && !p.folded && !p.isAllIn) {
          newPlayers[i] = { ...p, hasActed: false };
        }
      });
    }
    
    set({
      players: newPlayers,
      pot: newPot,
      currentBet: newCurrentBet,
      minRaise: newMinRaise,
      lastRaiseAmount: newLastRaise,
      actionLog: newLog
    });
    
    // Check if betting round is complete
    setTimeout(() => get().checkBettingComplete(), 300);
  },
  
  // Check if betting round is complete
  checkBettingComplete: () => {
    const { players, currentBet, currentPlayerIndex } = get();
    
    const activePlayers = players.filter(p => !p.folded && !p.isAllIn);
    const playersInHand = players.filter(p => !p.folded);
    
    // If only one player left, they win
    if (playersInHand.length === 1) {
      get().endHandEarly(playersInHand[0]);
      return;
    }
    
    // Check if all active players have acted and matched the bet
    const allMatched = activePlayers.every(p => p.hasActed && p.currentBet === currentBet);
    
    if (allMatched || activePlayers.length === 0) {
      // Betting complete, go to showdown
      get().goToShowdown();
    } else {
      // Move to next player
      get().nextPlayer();
    }
  },
  
  // Move to next player
  nextPlayer: () => {
    const { players, currentPlayerIndex } = get();
    
    let nextIndex = (currentPlayerIndex + 1) % players.length;
    let attempts = 0;
    
    // Find next active player
    while ((players[nextIndex].folded || players[nextIndex].isAllIn || 
           (players[nextIndex].hasActed && players[nextIndex].currentBet === get().currentBet)) 
           && attempts < players.length) {
      nextIndex = (nextIndex + 1) % players.length;
      attempts++;
    }
    
    if (attempts >= players.length) {
      // No more players to act
      get().goToShowdown();
      return;
    }
    
    set({
      currentPlayerIndex: nextIndex,
      message: `${players[nextIndex].name}'s turn`
    });
    
    // If AI's turn, trigger AI action
    if (!players[nextIndex].isHuman) {
      setTimeout(() => get().aiAction(), 1000);
    }
  },
  
  // AI decision making
  aiAction: () => {
    const { players, currentPlayerIndex, currentBet, bigBlind } = get();
    const player = players[currentPlayerIndex];
    
    if (player.isHuman || player.folded || player.isAllIn) return;
    
    const hand = evaluateThreeCardHand(player.cards);
    const toCall = currentBet - player.currentBet;
    const potOdds = toCall / (get().pot + toCall);
    
    // AI strategy based on hand strength
    const handStrength = hand.hand.rank / 6; // 0-1 scale
    
    let action = ACTIONS.FOLD;
    let raiseAmount = 0;
    
    if (toCall === 0) {
      // No bet to call
      if (handStrength > 0.5) {
        // Strong hand - raise
        action = ACTIONS.RAISE;
        raiseAmount = bigBlind * (1 + Math.floor(handStrength * 3));
      } else {
        // Weak hand - check
        action = ACTIONS.CHECK;
      }
    } else {
      // There's a bet to call
      if (handStrength > 0.6) {
        // Strong hand - raise sometimes
        if (Math.random() < handStrength) {
          action = ACTIONS.RAISE;
          raiseAmount = Math.min(toCall + bigBlind * 2, player.chips - toCall);
          if (raiseAmount <= 0) action = ACTIONS.CALL;
        } else {
          action = ACTIONS.CALL;
        }
      } else if (handStrength > 0.3 && potOdds < handStrength) {
        // Decent hand, good pot odds - call
        action = ACTIONS.CALL;
      } else if (handStrength > 0.2 && toCall <= bigBlind) {
        // Marginal hand, small bet - call sometimes
        action = Math.random() < 0.5 ? ACTIONS.CALL : ACTIONS.FOLD;
      } else {
        // Weak hand - fold
        action = ACTIONS.FOLD;
      }
    }
    
    // Ensure action is valid
    if (action === ACTIONS.CALL && player.chips < toCall) {
      action = player.chips > 0 ? ACTIONS.ALL_IN : ACTIONS.FOLD;
    }
    if (action === ACTIONS.RAISE && raiseAmount <= 0) {
      action = ACTIONS.CALL;
    }
    
    get().executeAction(action, raiseAmount);
  },
  
  // End hand early (everyone folded)
  endHandEarly: (winner) => {
    const { pot, actionLog, handsPlayed, handsWon, players } = get();
    
    const newPlayers = players.map(p => 
      p.id === winner.id ? { ...p, chips: p.chips + pot } : p
    );
    
    const newLog = [...actionLog, `${winner.name} wins ${pot} (everyone else folded)`];
    
    PokerSounds.playWin();
    
    set({
      players: newPlayers,
      winners: [winner],
      gamePhase: GAME_PHASES.RESULT,
      actionLog: newLog,
      message: `${winner.name} wins ${pot}!`,
      handsPlayed: handsPlayed + 1,
      handsWon: winner.isHuman ? handsWon + 1 : handsWon
    });
  },
  
  // Go to showdown
  goToShowdown: () => {
    const { players } = get();
    
    // Evaluate all hands
    const playersWithHands = players.map(p => ({
      ...p,
      hand: p.folded ? null : evaluateThreeCardHand(p.cards)
    }));
    
    set({
      players: playersWithHands,
      gamePhase: GAME_PHASES.SHOWDOWN,
      message: 'Showdown!'
    });
    
    PokerSounds.playFlip();
    
    // Reveal and determine winner after delay
    setTimeout(() => {
      set({ showdownRevealed: true });
      setTimeout(() => get().determineWinner(), 1500);
    }, 500);
  },
  
  // Determine winner
  determineWinner: () => {
    const { players, pot, actionLog, handsPlayed, handsWon } = get();
    
    const activePlayers = players.filter(p => !p.folded);
    
    // Find best hand(s)
    let bestScore = -1;
    let winners = [];
    
    activePlayers.forEach(p => {
      if (p.hand && p.hand.score > bestScore) {
        bestScore = p.hand.score;
        winners = [p];
      } else if (p.hand && p.hand.score === bestScore) {
        winners.push(p);
      }
    });
    
    // Distribute pot
    const share = Math.floor(pot / winners.length);
    const newPlayers = players.map(p => {
      const isWinner = winners.some(w => w.id === p.id);
      return isWinner ? { ...p, chips: p.chips + share } : p;
    });
    
    const winnerNames = winners.map(w => w.name).join(', ');
    const winningHand = winners[0]?.hand?.hand?.name || 'Unknown';
    const newLog = [...actionLog, `${winnerNames} wins ${share} with ${winningHand}`];
    
    const humanWon = winners.some(w => w.isHuman);
    
    if (humanWon) {
      PokerSounds.playWin();
    } else {
      PokerSounds.playLose();
    }
    
    set({
      players: newPlayers,
      winners,
      gamePhase: GAME_PHASES.RESULT,
      actionLog: newLog,
      message: `${winnerNames} wins with ${winningHand}!`,
      handsPlayed: handsPlayed + 1,
      handsWon: humanWon ? handsWon + 1 : handsWon
    });
  },
  
  // Reset for next hand
  nextHand: () => {
    set({
      gamePhase: GAME_PHASES.WAITING,
      message: 'Click "Deal" to start a new hand'
    });
  },
  
  // Reset game completely
  resetGame: () => {
    set(INITIAL_STATE);
  },
  
  // Add chips (for testing/rebuy)
  addChips: (playerId, amount) => {
    const { players } = get();
    const newPlayers = players.map(p => 
      p.id === playerId ? { ...p, chips: p.chips + amount } : p
    );
    set({ players: newPlayers });
  }
}));

export default useThreeCardStore;
